import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../Firebase/Firebase"; // Adjust path to firebase.js
import { Dropdown, Container, Alert, Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import "./News.css";
import TabsContainer from "./TabContainer";
import Logo from "./Logo.png";
import LoadingScreen from './LoadingScreen';
import NewsletterFooter from "./NewsletterFooter";
import axios from "axios";
import { BACKEND_BASE } from "../../config/api";
import {
  collectionNamesForDate,
  fetchPublishedContentStatus,
  formatEditionLongDate,
} from "../../lib/contentDate";
import { discoverArchiveDateKeys } from "../../lib/discoverArchiveDates";

/** Used if /languages fails (e.g. backend offline); keep in sync with server fallback where practical. */
const LANGUAGES_CLIENT_FALLBACK = [
  { code: "en", name: "English" },
  { code: "es", name: "Spanish" },
  { code: "fr", name: "French" },
  { code: "de", name: "German" },
  { code: "zh-Hans", name: "Chinese" },
  { code: "hi", name: "Hindi" },
];

function migrateLanguageCode(prev, langs) {
  if (prev == null || !langs || langs.length === 0) return prev;
  const codes = new Set(langs.map((l) => l.code));
  if (codes.has(prev)) return prev;
  if (prev === "zh" && codes.has("zh-Hans")) return "zh-Hans";
  if (prev === "no" && codes.has("nb")) return "nb";
  if (codes.has("en")) return "en";
  return langs[0]?.code ?? prev;
}

function LanguageSelectMenu({ languages }) {
  return (
    <Dropdown.Menu style={{ maxHeight: "500px", overflowY: "auto" }}>
      {languages.length === 0 ? (
        <Dropdown.Header className="text-muted small">Loading languages…</Dropdown.Header>
      ) : (
        languages.map((lang) => (
          <Dropdown.Item key={lang.code} eventKey={lang.code}>
            {lang.name}
          </Dropdown.Item>
        ))
      )}
    </Dropdown.Menu>
  );
}

const News = () => {
  const { dateKey: urlDateKey } = useParams();
  const navigate = useNavigate();
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    try {
      return localStorage.getItem("selectedLanguage");
    } catch {
      return null;
    }
  });
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [newsSummaries, setNewsSummaries] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(() => {
    try {
      return Boolean(localStorage.getItem("selectedLanguage"));
    } catch {
      return false;
    }
  });
  const [showModal, setShowModal] = useState(false);
  const [gameWords, setGameWords] = useState([]); // [{word, translations: {en, es, ...}}]
  const [translationsReady, setTranslationsReady] = useState(false);
  const [isTodayLive, setIsTodayLive] = useState(true);
  const [contentPreparing, setContentPreparing] = useState(false);
  const [londonToday, setLondonToday] = useState(null);
  const [publishedDateKey, setPublishedDateKey] = useState(null);
  const [selectedDateKey, setSelectedDateKey] = useState(null);
  const [archiveDateKeys, setArchiveDateKeys] = useState([]);

  useEffect(() => {
    if (!localStorage.getItem("selectedLanguage")) {
      setShowModal(true);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_BASE}/languages`);
        const langs = Array.isArray(data?.languages) ? data.languages : [];
        if (cancelled) return;
        if (langs.length === 0) {
          const fb = LANGUAGES_CLIENT_FALLBACK;
          setAvailableLanguages(fb);
          setSelectedLanguage((prev) => {
            try {
              const next = migrateLanguageCode(prev, fb);
              if (next !== prev && next != null) {
                localStorage.setItem("selectedLanguage", next);
              }
              return next;
            } catch {
              return prev;
            }
          });
          return;
        }
        setAvailableLanguages(langs);
        setSelectedLanguage((prev) => {
          try {
            const next = migrateLanguageCode(prev, langs);
            if (next !== prev && next != null) {
              localStorage.setItem("selectedLanguage", next);
            }
            return next;
          } catch {
            return prev;
          }
        });
      } catch (e) {
        console.error("Failed to load languages:", e);
        if (!cancelled) {
          const fb = LANGUAGES_CLIENT_FALLBACK;
          setAvailableLanguages(fb);
          setSelectedLanguage((prev) => {
            try {
              const next = migrateLanguageCode(prev, fb);
              if (next !== prev && next != null) {
                localStorage.setItem("selectedLanguage", next);
              }
              return next;
            } catch {
              return prev;
            }
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Resolve which edition is live (published date, job status).
  useEffect(() => {
    if (selectedLanguage == null) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const status = await fetchPublishedContentStatus();
        if (cancelled) return;
        setLondonToday(status.londonToday);
        setPublishedDateKey(status.publishedDateKey);
        setIsTodayLive(status.isTodayLive);
        setContentPreparing(status.jobStatus === "running");
        setSelectedDateKey((prev) => {
          if (urlDateKey) return urlDateKey;
          return prev ?? status.publishedDateKey;
        });
      } catch (statusErr) {
        console.error("Failed to resolve published content date:", statusErr);
        if (!cancelled) {
          setError("Could not determine today's edition. Please try again later.");
          setIsLoading(false);
          setTranslationsReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedLanguage, urlDateKey]);

  useEffect(() => {
    if (urlDateKey) {
      setSelectedDateKey(urlDateKey);
    }
  }, [urlDateKey]);

  // Discover breadcrumb dates directly from Firestore (no backend dependency).
  useEffect(() => {
    if (!londonToday) return;

    let cancelled = false;
    (async () => {
      try {
        const keys = await discoverArchiveDateKeys(db, londonToday);
        if (!cancelled) setArchiveDateKeys(keys);
      } catch (err) {
        console.error("Failed to discover archive dates:", err);
        if (!cancelled) setArchiveDateKeys([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [londonToday]);

  // Load articles and game words for the selected edition date.
  useEffect(() => {
    const loadEditionContent = async () => {
      if (selectedLanguage == null || selectedDateKey == null) {
        setIsLoading(false);
        return;
      }

      const startTime = performance.now();
      setIsLoading(true);
      setError("");
      setTranslationsReady(false);
      setNewsSummaries([]);
      setGameWords([]);

      try {
        const { news: collectionName, gameWords: gameWordsCollectionName } =
          collectionNamesForDate(selectedDateKey);

        const [newsCollection, gameWordsCollection] = await Promise.all([
          getDocs(collection(db, collectionName)),
          getDocs(collection(db, gameWordsCollectionName)),
        ]);

        const news = newsCollection.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .slice(0, 10);
        const gameWordsArr = gameWordsCollection.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .sort((a, b) => Number(String(a.id)) - Number(String(b.id)));

        if (news.length === 0) {
          setNewsSummaries([]);
          setGameWords([]);
          const isCurrentPublished =
            publishedDateKey != null && selectedDateKey === publishedDateKey;
          setError(
            isCurrentPublished && contentPreparing
              ? "Today's edition is being prepared. Please check back shortly."
              : `No articles available for ${formatEditionLongDate(selectedDateKey)}.`
          );
          setTranslationsReady(true);
          return;
        }

        setNewsSummaries(news);
        setGameWords(gameWordsArr);
        setTranslationsReady(true);
        console.log(
          `Loaded ${news.length} articles for ${selectedDateKey} in ${(performance.now() - startTime).toFixed(0)}ms`
        );
      } catch (err) {
        console.error("Failed to load edition content:", err);
        setError("Failed to load news.");
        setTranslationsReady(true);
      } finally {
        setIsLoading(false);
      }
    };
    loadEditionContent();
  }, [selectedLanguage, selectedDateKey, publishedDateKey, contentPreparing]);

  const handleSelectEditionDate = useCallback(
    (dateKey) => {
      setSelectedDateKey(dateKey);
      if (dateKey === londonToday || dateKey === publishedDateKey) {
        navigate("/");
      } else {
        navigate(`/edition/${dateKey}`);
      }
    },
    [navigate, londonToday, publishedDateKey]
  );

  // Handle language selection
  const handleLanguageChange = (languageCode) => {
    setSelectedLanguage(languageCode);
    localStorage.setItem("selectedLanguage", languageCode); // Store the selection in localStorage
    setShowModal(false); // Close the modal after selection
  };

  const handleTranslationsReady = useCallback(() => {
    setTranslationsReady(true);
  }, []);

  const viewingArchiveEdition =
    selectedDateKey != null &&
    publishedDateKey != null &&
    selectedDateKey !== publishedDateKey;

  const loadingHeadline = viewingArchiveEdition
    ? `Loading ${formatEditionLongDate(selectedDateKey)}…`
    : contentPreparing && !isTodayLive
      ? "Showing yesterday's edition while today's is prepared…"
      : "Loading today's edition…";

  const languageSelectLabel =
    availableLanguages.find((lang) => lang.code === selectedLanguage)?.name || "Select Language";

  return (
    <main className="position-relative">
      <header className="news-header">
        <div className="news-header-lang">
          <Dropdown onSelect={handleLanguageChange}>
            <Dropdown.Toggle
              variant="outline-secondary"
              size="sm"
              id="language-selector-toolbar"
              className="news-outline-btn text-start text-truncate"
              title="Change study language"
              aria-label="Change study language"
            >
              {languageSelectLabel}
            </Dropdown.Toggle>
            <LanguageSelectMenu languages={availableLanguages} />
          </Dropdown>
        </div>

        <div className="news-header-brand">
          <h1 className="visually-hidden">The Daily Lingo — daily news and puzzles for language learning</h1>
          <img src={Logo} alt="The Daily Lingo" className="daily-lingo-logo" />
          <p className="news-tagline">
            Daily news and puzzles to help your language learning.
          </p>
          <Alert variant="warning" className="news-site-update-banner news-light-yellow-alert">
            <p className="news-site-update-banner-text mb-0">
              Futher updates to site are being carried out. From new multiplayer games to
              personalised in depth news articles. Join the news letter to watch the progress.
            </p>
            <Button
              href="#newsletter-signup"
              variant="dark"
              size="sm"
              className="news-site-update-banner-btn"
            >
              Subscribe
            </Button>
          </Alert>
        </div>
      </header>
      <div style={{ borderTop: "1px solid lightGrey" }}></div>

      <Container className="mt-4">
        {selectedLanguage && isLoading && !error && newsSummaries.length === 0 && (
          <LoadingScreen
            showProgressBar={false}
            headline={loadingHeadline}
          />
        )}
        {error ? (
          <Alert variant="warning" className="mt-3">
            {error}
          </Alert>
        ) : null}
        {viewingArchiveEdition && !isLoading && !error && newsSummaries.length > 0 && (
          <Alert variant="warning" className="mt-3 mb-0 news-light-yellow-alert">
            Viewing the edition from {formatEditionLongDate(selectedDateKey)}. Use the dates
            below the News tab to return to a more recent day.
          </Alert>
        )}
        {!error && selectedLanguage && selectedDateKey && londonToday && (
          <TabsContainer 
            newsSummaries={newsSummaries} 
            selectedLanguage={selectedLanguage} 
            gameWords={gameWords}
            onTranslationsReady={handleTranslationsReady}
            archiveDateKeys={archiveDateKeys}
            selectedDateKey={selectedDateKey}
            londonToday={londonToday}
            onSelectDateKey={handleSelectEditionDate}
            editionLoading={isLoading}
          />
        )}
      </Container>

      <NewsletterFooter />

      {/* Modal for selecting language — must pick a language; backdrop / Esc do not close */}
      <Modal
        show={showModal}
        onHide={() => {}}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Header closeButton={false}>
          <Modal.Title>Please Select Your Target Language</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Dropdown onSelect={handleLanguageChange}>
            <Dropdown.Toggle variant="secondary" id="language-selector-modal">
              {languageSelectLabel}
            </Dropdown.Toggle>
            <LanguageSelectMenu languages={availableLanguages} />
          </Dropdown>
        </Modal.Body>
      </Modal>
    </main>
  );
};

export default News;
